import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Search, Edit, Trash2, Pill } from "lucide-react";
import {
  drugGenericService,
  DrugGeneric,
} from "../../services/drug-generic.service";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export default function DrugGenericsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
  }>({
    isOpen: false,
    id: "",
    name: "",
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["drug-generics", { search, page }],
    queryFn: () => drugGenericService.getAll({ search, page, limit: 20 }),
  });

  const deleteMutation = useMutation({
    mutationFn: drugGenericService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drug-generics"] });
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(deleteModal.id);
    setDeleteModal({ isOpen: false, id: "", name: "" });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Pill className="h-6 w-6" />
          Drug Generics
        </h1>
        <Link
          to="/drug-generics/create"
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Generic
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search generics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Schedule
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Form
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Products
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((generic: DrugGeneric) => (
                  <tr key={generic.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{generic.name}</div>
                      {generic.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {generic.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{generic.code || "-"}</td>
                    <td className="px-4 py-3 text-sm">
                      {generic.scheduleType && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                          {generic.scheduleType}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {generic.form?.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {generic._count?.products || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          generic.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {generic.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/drug-generics/${generic.id}/edit`}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() =>
                            handleDeleteClick(generic.id, generic.name)
                          }
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to{" "}
              {Math.min(page * 20, data.pagination.total)} of{" "}
              {data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(data.pagination.totalPages, p + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", name: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete Generic"
        message={`Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
